import { useId, useState } from "react";
import { Modal } from "./Modal.js";
import type { Door } from "../types/api.js";

/** A persistent override is not a thing to reach with a bare "OK". This dialog says, in words,
 * exactly what it does - survives a power cycle, suspends fingerprint and card verification,
 * stays until someone uses Release Override - and makes the operator type the door's name
 * before the destructive button becomes usable.
 *
 * Never window.confirm(): it cannot explain what the override means. */
export function OverrideConfirm({
  door,
  action,
  busy,
  onCancel,
  onConfirm,
}: {
  door: Door;
  action: "hold_unlock" | "hold_lock";
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const label = action === "hold_unlock" ? "HOLD UNLOCKED" : "HOLD LOCKED";
  const matches = typed.trim().toLowerCase() === door.name.trim().toLowerCase();

  return (
    <Modal
      title={`Put ${door.name} into ${label}?`}
      onClose={onCancel}
      tone="critical"
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={!matches || busy}
          >
            {busy ? "Sending…" : `Yes — ${label.toLowerCase()} this door`}
          </button>
        </>
      }
    >
      <p>
        <strong>This is a persistent override.</strong> It survives a power cycle and suspends
        fingerprint and card verification. The door stays{" "}
        {action === "hold_unlock" ? "unlocked" : "locked"} until someone uses{" "}
        <strong>Release Override</strong>.
      </p>

      {action === "hold_unlock" ? (
        <p>
          While this is in place, <strong>anyone can walk through this door</strong> without
          presenting a finger or a card.
        </p>
      ) : (
        <p>
          While this is in place, <strong>nobody can open this door with a finger or a card</strong>
          , including people who normally have access.
        </p>
      )}

      <div className="field">
        <label htmlFor={inputId}>
          Type the door&apos;s name to confirm: <code>{door.name}</code>
        </label>
        <input
          id={inputId}
          type="text"
          value={typed}
          autoComplete="off"
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>

      <p className="modal-meta">
        Controller {door.controllerAddress} · device {door.deviceId}
      </p>
    </Modal>
  );
}
